import { motion } from 'framer-motion';
import { Trophy, Flame, BookOpen, Star, Target, ClipboardCheck, CheckCircle2, Circle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { TeamMember } from '@/hooks/useCoach';
import type { AssignmentCompletion } from '@/hooks/useAssignmentCompletion';
import { getLessonsForSport } from './LessonPicker';

interface PlayerDetailExpandedProps {
  member: TeamMember;
  sport: string;
  assignmentProgress?: AssignmentCompletion['players'][number];
}

/**
 * Inline expanded view for a roster row. Shows full player stats,
 * curriculum progress, assigned-content progress, completed history,
 * and remaining lessons. Designed to live inside a glass-card row.
 */
export default function PlayerDetailExpanded({ member, sport, assignmentProgress }: PlayerDetailExpandedProps) {
  const { allLessons } = getLessonsForSport(sport);
  const completedSet = new Set(member.completed_lessons || []);
  const completedLessons = allLessons.filter(l => completedSet.has(l.id));
  const remainingLessons = allLessons.filter(l => !completedSet.has(l.id));
  const completionPct = allLessons.length > 0
    ? Math.round((completedLessons.length / allLessons.length) * 100)
    : 0;

  const assignedDone = assignmentProgress?.completed ?? 0;
  const assignedTotal = assignmentProgress?.assigned ?? 0;
  const assignedPct = assignmentProgress?.pct ?? 0;

  const stats = [
    { icon: Trophy, label: 'Total XP', value: (member.xp || 0).toLocaleString(), color: 'text-primary',     bg: 'bg-primary/15 ring-primary/30' },
    { icon: Star,   label: 'Level',    value: `Lv.${member.level || 0}`,         color: 'text-violet-300',  bg: 'bg-violet-500/15 ring-violet-400/30' },
    { icon: Flame,  label: 'Streak',   value: `${member.streak || 0}`,           color: 'text-orange-300',  bg: 'bg-orange-500/15 ring-orange-400/30' },
    { icon: BookOpen, label: 'Lessons', value: `${completedLessons.length}`,     color: 'text-emerald-300', bg: 'bg-emerald-500/15 ring-emerald-400/30' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2">
          {stats.map(s => (
            <div key={s.label} className="rounded-xl bg-white/[0.03] ring-1 ring-white/5 p-2.5 text-center">
              <div className={`w-7 h-7 rounded-lg ring-1 mx-auto flex items-center justify-center mb-1 ${s.bg}`}>
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <p className={`font-display font-bold text-sm leading-none ${s.color}`}>{s.value}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-body mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Assigned content progress */}
        {assignedTotal > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <h5 className="font-display font-bold text-xs flex items-center gap-1.5 text-foreground">
                <ClipboardCheck className="w-3.5 h-3.5 text-orange-300" /> Assigned content
              </h5>
              <span className="text-[10px] font-display font-bold text-muted-foreground">
                {assignedDone} / {assignedTotal} · {assignedPct}%
              </span>
            </div>
            <Progress value={assignedPct} className="h-1.5" />
          </div>
        )}

        {/* Curriculum progress */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h5 className="font-display font-bold text-xs flex items-center gap-1.5 text-foreground">
              <Target className="w-3.5 h-3.5 text-primary" /> Curriculum progress
            </h5>
            <span className="text-[10px] font-display font-bold text-muted-foreground">
              {completedLessons.length} / {allLessons.length} · {completionPct}%
            </span>
          </div>
          <Progress value={completionPct} className="h-1.5" />
        </div>

        {/* Completed lesson history */}
        <div>
          <h5 className="font-display font-bold text-xs mb-1.5 text-foreground">Lesson history</h5>
          {completedLessons.length === 0 ? (
            <p className="text-[11px] text-muted-foreground font-body italic">
              No lessons completed yet.
            </p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {completedLessons.slice().reverse().map(lesson => (
                <div key={lesson.id} className="flex items-center gap-2 py-1 px-2 rounded-md bg-white/[0.03]">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                  <span className="text-[11px] font-body text-foreground/90 truncate">{lesson.title}</span>
                  <span className="text-[9px] font-display font-bold text-primary ml-auto shrink-0">
                    +{lesson.xpReward} XP
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Remaining (preview) */}
        {remainingLessons.length > 0 && (
          <div>
            <h5 className="font-display font-bold text-xs mb-1.5 text-muted-foreground">
              Remaining ({remainingLessons.length})
            </h5>
            <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
              {remainingLessons.slice(0, 8).map(lesson => (
                <div key={lesson.id} className="flex items-center gap-2 py-1 px-2 rounded-md">
                  <Circle className="w-3 h-3 text-muted-foreground/40 shrink-0" />
                  <span className="text-[11px] font-body text-muted-foreground truncate">{lesson.title}</span>
                </div>
              ))}
              {remainingLessons.length > 8 && (
                <p className="text-[10px] text-muted-foreground font-body text-center pt-1">
                  +{remainingLessons.length - 8} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
